<template>
  <div class="gear-atmosphere" aria-hidden="true">
    <div class="atmosphere-glow atmosphere-glow-top"></div>
    <div class="atmosphere-glow atmosphere-glow-bottom"></div>
    <div class="background-arc background-arc-a" :style="arcAStyle"></div>
    <div class="background-arc background-arc-b" :style="arcBStyle"></div>
    <svg class="gear-canvas" :viewBox="`0 0 ${viewport.width} ${viewport.height}`" preserveAspectRatio="none">
      <defs>
        <path
          id="centerfire-large-gear"
          d="M87.665,-7.670L99.803,-6.279A100,100 0 0 1 99.803,6.279L87.665,7.670A88,88 0 0 1 85.001,22.776L95.931,28.234A100,100 0 0 1 91.636,40.035L79.755,37.190A88,88 0 0 1 72.085,50.475L80.489,59.342A100,100 0 0 1 72.417,68.962L62.225,62.225A88,88 0 0 1 50.475,72.085L55.339,83.292A100,100 0 0 1 44.464,89.571L37.190,79.755A88,88 0 0 1 22.776,85.001L23.514,97.196A100,100 0 0 1 11.147,99.377L7.670,87.665A88,88 0 0 1 -7.670,87.665L-11.147,99.377A100,100 0 0 1 -23.514,97.196L-22.776,85.001A88,88 0 0 1 -37.190,79.755L-44.464,89.571A100,100 0 0 1 -55.339,83.292L-50.475,72.085A88,88 0 0 1 -62.225,62.225L-72.417,68.962A100,100 0 0 1 -80.489,59.342L-72.085,50.475A88,88 0 0 1 -79.755,37.190L-91.636,40.035A100,100 0 0 1 -95.931,28.234L-85.001,22.776A88,88 0 0 1 -87.665,7.670L-99.803,6.279A100,100 0 0 1 -99.803,-6.279L-87.665,-7.670A88,88 0 0 1 -85.001,-22.776L-95.931,-28.234A100,100 0 0 1 -91.636,-40.035L-79.755,-37.190A88,88 0 0 1 -72.085,-50.475L-80.489,-59.342A100,100 0 0 1 -72.417,-68.962L-62.225,-62.225A88,88 0 0 1 -50.475,-72.085L-55.339,-83.292A100,100 0 0 1 -44.464,-89.571L-37.190,-79.755A88,88 0 0 1 -22.776,-85.001L-23.514,-97.196A100,100 0 0 1 -11.147,-99.377L-7.670,-87.665A88,88 0 0 1 7.670,-87.665L11.147,-99.377A100,100 0 0 1 23.514,-97.196L22.776,-85.001A88,88 0 0 1 37.190,-79.755L44.464,-89.571A100,100 0 0 1 55.339,-83.292L50.475,-72.085A88,88 0 0 1 62.225,-62.225L72.417,-68.962A100,100 0 0 1 80.489,-59.342L72.085,-50.475A88,88 0 0 1 79.755,-37.190L91.636,-40.035A100,100 0 0 1 95.931,-28.234L85.001,-22.776Z"
        />
        <path
          id="centerfire-pinion"
          d="M24.470,-6.557L32.743,-6.246A33.333,33.333 0 0 1 32.743,6.246L24.470,6.557A25.333,25.333 0 0 1 17.913,17.913L21.781,25.233A33.333,33.333 0 0 1 10.962,31.479L6.557,24.470A25.333,25.333 0 0 1 -6.557,24.470L-10.962,31.479A33.333,33.333 0 0 1 -21.781,25.233L-17.913,17.913A25.333,25.333 0 0 1 -24.470,6.557L-32.743,6.246A33.333,33.333 0 0 1 -32.743,-6.246L-24.470,-6.557A25.333,25.333 0 0 1 -17.913,-17.913L-21.781,-25.233A33.333,33.333 0 0 1 -10.962,-31.479L-6.557,-24.470A25.333,25.333 0 0 1 6.557,-24.470L10.962,-31.479A33.333,33.333 0 0 1 21.781,-25.233L17.913,-17.913Z"
        />
      </defs>

      <g
        v-for="set in gearSets"
        :key="set.key"
        class="gear-set"
        :class="`gear-set-${set.key}`"
        :transform="`translate(${motion[set.key].x} ${motion[set.key].y}) scale(${motion[set.key].scale})`"
      >
        <g :transform="`rotate(${motion[set.key].bigRotation})`">
          <use href="#centerfire-large-gear" />
        </g>
        <g :transform="`translate(${motion[set.key].pinionX} ${motion[set.key].pinionY})`">
          <g :transform="`rotate(${motion[set.key].pinionRotation})`">
            <use class="pinion" href="#centerfire-pinion" />
          </g>
        </g>
      </g>
    </svg>
  </div>
</template>

<script>
const CENTER_DISTANCE = 125.333;
const PINION_TIP = 33.333;

/* The artwork fixes these, so they are not tuning knobs. The big gear carries 18
   teeth and the pinion 6, which is a 3:1 ratio and the reason CENTER_DISTANCE is
   125.333 — the pitch radii are 94 and 31.333. A pinion carried around its gear
   by `theta` while that gear turns by `bigRotation` must then spin by
   (1 + 3)·theta − 3·bigRotation or the teeth visibly slip through each other,
   and MESH_PHASE is the half tooth (360 / 6 / 2) that drops them into the gaps.
   Every set gets the mesh from this; a set only ever chooses theta and turn. */
const GEAR_RATIO = 3;
const MESH_PHASE = 30;

/* One entry per set, and deliberately no two alike: scroll window, lane, scale,
   spin and pinion orbit all differ, so four sets crossing the page never read as
   one set copy-pasted. `turn` is degrees of gear spin across the pass and its
   sign is the direction. `sweep` is where the pinion orbits, in degrees, and a
   descending pair orbits it the other way round. `travel` is which edge the set
   enters from. `stopAt` is the one that matters at the foot of the page: a set
   without it clears the top edge and is gone, while a set with it halts partway
   up, still in frame when the scroll runs out.

   The windows are fractions of total page scroll, so adding a section below
   retimes every set — worth a re-check whenever the page grows. */
const GEAR_SETS = Object.freeze([
  {
    key: 'primary',
    enter: 0.01,
    span: 0.56,
    lane: (width) => width * 0.51,
    scale: 1,
    turn: -34,
    sweep: [-90, 90],
    travel: 'up',
  },
  {
    key: 'secondary',
    enter: 0.43,
    span: 0.56,
    lane: (width, base) => width - base * 38,
    scale: 0.86,
    turn: -30,
    // Cropped by the right edge, so the pinion works the left flank — orbiting
    // the right one, as it used to, kept it off screen for the whole pass.
    sweep: [104, 256],
    travel: 'up',
  },
  {
    key: 'tertiary',
    // Takes over where the primary clears the top and runs to the very end,
    // falling against the other three and turning the opposite way.
    enter: 0.5,
    span: 0.5,
    lane: (width) => width * 0.17,
    scale: 0.62,
    turn: 47,
    sweep: [128, -46],
    travel: 'down',
  },
  {
    key: 'quaternary',
    // The closer, and the largest. It never finishes its pass: at the foot of
    // the page it is still rising through the bottom edge, so the machinery
    // reads as carrying on past the end rather than stopping at it.
    enter: 0.72,
    span: 0.28,
    lane: (width) => width * 0.68,
    scale: 1.1,
    turn: -19,
    sweep: [156, 238],
    travel: 'up',
    stopAt: 0.38,
  },
]);

const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
const emptyGearSet = () => ({
  x: 0,
  y: 0,
  scale: 1,
  bigRotation: 0,
  pinionX: 0,
  pinionY: 0,
  pinionRotation: 0,
});
const emptyMotion = () => Object.fromEntries(GEAR_SETS.map((set) => [set.key, emptyGearSet()]));

export default {
  data() {
    return {
      frame: 0,
      viewport: { width: 1, height: 1 },
      gearSets: GEAR_SETS,
      motion: emptyMotion(),
      arcA: { x: 0, y: 0 },
      arcB: { x: 0, y: 0 },
      reduceMotion: false,
    };
  },
  computed: {
    arcAStyle() {
      return { transform: `translate3d(${this.arcA.x}px, ${this.arcA.y}px, 0)` };
    },
    arcBStyle() {
      return { transform: `translate3d(${this.arcB.x}px, ${this.arcB.y}px, 0)` };
    },
  },
  mounted() {
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.updateMotion();
    window.addEventListener('scroll', this.requestUpdate, { passive: true });
    window.addEventListener('resize', this.requestUpdate, { passive: true });
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.requestUpdate);
    window.removeEventListener('resize', this.requestUpdate);
    window.cancelAnimationFrame(this.frame);
  },
  methods: {
    requestUpdate() {
      if (this.frame) return;
      this.frame = window.requestAnimationFrame(() => {
        this.frame = 0;
        this.updateMotion();
      });
    },
    setGearMotion(target, set, progress, width, base) {
      const scale = base * set.scale;
      const radiusToEdge = (CENTER_DISTANCE + PINION_TIP) * scale;
      const height = this.viewport.height;
      const diameter = radiusToEdge * 2;

      /* `reveal` is how far the set has crossed the bottom edge, measured in its
         own diameters: 0 parks it just out of sight below the screen and `clear`
         puts it just past the top. Endpoints live in this space so a set can be
         told to stop partway up and stay in frame, which raw pixels could not
         express without knowing the viewport at author time. */
      const clear = (height + diameter) / diameter;
      const [from, to] = set.travel === 'down' ? [clear, 0] : [0, set.stopAt ?? clear];
      const reveal = from + (to - from) * progress;

      const theta = set.sweep[0] + (set.sweep[1] - set.sweep[0]) * progress;
      const thetaRadians = (theta * Math.PI) / 180;
      const bigRotation = set.turn * progress;

      target.x = set.lane(width, base);
      target.y = height + radiusToEdge - diameter * reveal;
      target.scale = scale;
      target.bigRotation = bigRotation;
      target.pinionX = CENTER_DISTANCE * Math.cos(thetaRadians);
      target.pinionY = CENTER_DISTANCE * Math.sin(thetaRadians);
      target.pinionRotation = (1 + GEAR_RATIO) * theta - GEAR_RATIO * bigRotation + MESH_PHASE;
    },
    updateMotion() {
      const width = window.innerWidth || 1;
      const height = window.innerHeight || 1;
      const pageRange = Math.max(1, document.documentElement.scrollHeight - height);
      const progress = clamp(window.scrollY / pageRange);
      const baseScale = (Math.min(width, height) * 0.34) / 100;

      this.viewport = { width, height };
      this.gearSets.forEach((set) => {
        const setProgress = clamp((progress - set.enter) / set.span);
        this.setGearMotion(this.motion[set.key], set, setProgress, width, baseScale);
      });

      if (this.reduceMotion) {
        this.arcA = { x: 0, y: 0 };
        this.arcB = { x: 0, y: 0 };
      } else {
        this.arcA = { x: progress * width * 0.22, y: progress * height * 0.2 };
        this.arcB = { x: -progress * width * 0.18, y: -progress * height * 0.24 };
      }
    },
  },
};
</script>

<style scoped>
.gear-atmosphere {
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  overflow: hidden;
}

.gear-canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: visible;
}

.gear-set {
  fill: rgba(0, 0, 0, 0.34);
  stroke: rgba(255, 90, 2, 0.32);
  stroke-width: 0.55;
  vector-effect: non-scaling-stroke;
  filter: drop-shadow(0 0 18px rgba(255, 90, 2, 0.14));
}

.gear-set-secondary {
  stroke: rgba(255, 255, 255, 0.18);
}

/* The lower half carries denser copy than the hero, so its two sets are pulled
   back: enough contrast to register as machinery still turning, not enough to
   compete with the text sitting on top of them. The quaternary is the largest
   set on the page and the faintest for that reason. */
.gear-set-tertiary {
  stroke: rgba(255, 90, 2, 0.26);
}

.gear-set-quaternary {
  stroke: rgba(255, 255, 255, 0.14);
}

.gear-set .pinion {
  fill: rgba(0, 0, 0, 0.5);
  stroke: rgba(255, 90, 2, 0.58);
}

.gear-set-quaternary .pinion {
  stroke: rgba(255, 90, 2, 0.4);
}

.atmosphere-glow,
.background-arc {
  position: absolute;
  border-radius: 50%;
}

.atmosphere-glow {
  filter: blur(90px);
}

.atmosphere-glow-top {
  width: 76vw;
  height: 58vh;
  right: -22vw;
  top: -22vh;
  background: radial-gradient(closest-side, rgba(255, 255, 255, 0.11), transparent 76%);
}

.atmosphere-glow-bottom {
  width: 150vw;
  height: 84vh;
  left: -28vw;
  bottom: -46vh;
  background: radial-gradient(closest-side, rgba(255, 90, 2, 0.28), rgba(255, 90, 2, 0.07) 58%, transparent 80%);
}

.background-arc {
  aspect-ratio: 1;
  will-change: transform;
}

.background-arc-a {
  width: 164vw;
  left: -77vw;
  top: -56vw;
  border: 1px solid rgba(255, 90, 2, 0.32);
  box-shadow: 0 0 42px rgba(255, 90, 2, 0.13), inset 0 0 160px rgba(255, 90, 2, 0.035);
}

.background-arc-b {
  width: 190vw;
  right: -112vw;
  bottom: -72vw;
  border: 1px solid rgba(255, 255, 255, 0.15);
  box-shadow: 0 0 34px rgba(255, 255, 255, 0.05);
}

@media (max-width: 760px) {
  .gear-set {
    stroke-width: 0.72;
  }

  .background-arc-a {
    width: 230vw;
    left: -126vw;
    top: -68vw;
  }

  .background-arc-b {
    width: 250vw;
    right: -156vw;
  }
}

@media (prefers-reduced-motion: reduce) {
  .background-arc {
    transform: none !important;
  }
}
</style>
